import { Injectable } from '@angular/core';
import { ObjectTypes } from '@fi-shared/types';
import { IDataFieldSection } from '@fi-app/data-field/data-field';
import { DataFieldPropertyType } from '@fi-app/data-field/data-field-property-type';
import { AppTextService } from '@fi-shared/app-text';
import { DataFieldModel } from '@fi-app/data-field/data-field.model';
import { DataFieldType } from '@fi-app/data-field/data-field-type';
import { BusinessUnitModel } from '@fi-app/business-unit/business-unit.model';
import { UserModel } from '@fi-app/user/user.model';
import { DataFieldSectionType } from '@fi-app/business-unit/data-field-section-type';

@Injectable()
export class DataFieldHelperService {
    dataFieldSectionMapping: { [prop: number]: DataFieldPropertyType[] } = {};
    constructor(private appTextService: AppTextService) {
        // Business Unit
        this.dataFieldSectionMapping[DataFieldSectionType.BusinessUnitInfo] = [
            DataFieldPropertyType.Name,
            DataFieldPropertyType.Description,
            DataFieldPropertyType.BusinessAreas,
            //DataFieldPropertyType.PKOrgNr,
        ];
        this.dataFieldSectionMapping[DataFieldSectionType.BusinessUnitLocationAndContacts] = [
            DataFieldPropertyType.Areas,
            DataFieldPropertyType.Cluster,
            DataFieldPropertyType.EmailAddress,
            DataFieldPropertyType.PhoneNumber,
            DataFieldPropertyType.Address,
        ];
        this.dataFieldSectionMapping[DataFieldSectionType.BusinessUnitViewGeneral] = [
            DataFieldPropertyType.Description,
            DataFieldPropertyType.Areas,
            DataFieldPropertyType.Cluster,
            DataFieldPropertyType.BusinessAreas,
            DataFieldPropertyType.PKOrgNr,
        ];
        // User
        this.dataFieldSectionMapping[DataFieldSectionType.UserName] = [DataFieldPropertyType.Name];
        this.dataFieldSectionMapping[DataFieldSectionType.Contacts] = [
            DataFieldPropertyType.EmailAddress,
            DataFieldPropertyType.PhoneNumber,
            DataFieldPropertyType.Address,
        ];
        this.dataFieldSectionMapping[DataFieldSectionType.UserWorkProfile] = [
            DataFieldPropertyType.Roles,
            DataFieldPropertyType.Units,
            DataFieldPropertyType.BusinessAreas,
            DataFieldPropertyType.Tags,
        ];
        this.dataFieldSectionMapping[DataFieldSectionType.UserSettings] = [DataFieldPropertyType.Languages];
        this.dataFieldSectionMapping[DataFieldSectionType.UserPassword] = [DataFieldPropertyType.Password];

        // Common
        this.dataFieldSectionMapping[DataFieldSectionType.AccountStatus] = [DataFieldPropertyType.Active];
        this.dataFieldSectionMapping[DataFieldSectionType.Custom] = [DataFieldPropertyType.Custom];
    }

    getDataFieldSections(objectType: ObjectTypes, dataFields: DataFieldModel[]) {
        const sections: IDataFieldSection[] = [];
        if (objectType === ObjectTypes.User) {
            sections.push({
                sectionName: this.appTextService.get('user.profile_info'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.UserName),
            });
            sections.push({
                sectionName: this.appTextService.get('user.user_status'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.AccountStatus),
            });
            sections.push({
                sectionName: this.appTextService.get('common.contact'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.Contacts),
            });
            sections.push({
                sectionName: this.appTextService.get('user.work_profile'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.UserWorkProfile),
            });
            sections.push({
                sectionName: this.appTextService.get('settings'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.UserSettings),
            });
            sections.push({
                sectionName: this.appTextService.get('common.security'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.UserPassword),
            });
            sections.push({
                sectionName: this.appTextService.get('common.other'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.Custom),
                isDragEnabled: true,
                isAddingEnabled: true,
            });
        } else if (objectType === ObjectTypes.BusinessUnit) {
            sections.push({
                sectionName: this.appTextService.get('business_unit.unit_info'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.BusinessUnitInfo),
            });
            sections.push({
                sectionName: this.appTextService.get('user.user_status'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.AccountStatus),
            });
            sections.push({
                sectionName: this.appTextService.get('business_unit.location_and_contact_tab'),
                dataFields: this.getDataFieldsBySectionType(
                    dataFields,
                    DataFieldSectionType.BusinessUnitLocationAndContacts,
                ),
            });
            sections.push({
                sectionName: this.appTextService.get('common.other'),
                dataFields: this.getDataFieldsBySectionType(dataFields, DataFieldSectionType.Custom),
                isDragEnabled: true,
                isAddingEnabled: true,
            });
        } else {
            throw new Error(`wrong objectType in getDataFieldSections: ${objectType}`);
        }
        return sections;
    }

    // todo use getDataFieldsBySectionType
    getDataFieldsByPropertyType(dataFields: DataFieldModel[], propertyType: DataFieldPropertyType): DataFieldModel[] {
        return this.getDataFieldsByPropertyTypes(dataFields, [propertyType]);
    }
    // todo use getDataFieldsBySectionType
    getDataFieldsByPropertyTypes(
        dataFields: DataFieldModel[],
        propertyTypes: DataFieldPropertyType[],
    ): DataFieldModel[] {
        return dataFields.filter((dataField) => propertyTypes.indexOf(dataField.PropertyType) !== -1);
    }
    getDataFieldsBySectionType(dataFields: DataFieldModel[], sectionType: DataFieldSectionType) {
        const propertyTypes = this.dataFieldSectionMapping[sectionType];
        return this.getDataFieldsByPropertyTypes(dataFields, propertyTypes);
    }

    getProfileViewDataFieldsMatIconClass(dataField: DataFieldModel): string {
        switch (dataField.PropertyType) {
            case DataFieldPropertyType.Description:
                return 'fas fa-file-lines';
            case DataFieldPropertyType.Areas:
                return 'fas fa-location-dot';
            case DataFieldPropertyType.Cluster:
                return 'fas fa-layer-group';
            case DataFieldPropertyType.BusinessAreas:
                return 'fas fa-building-columns';
            case DataFieldPropertyType.EmailAddress:
                return 'fas fa-envelopes-bulk';
            case DataFieldPropertyType.PhoneNumber:
                return 'fas fa-phone';
            case DataFieldPropertyType.Address:
                return 'fas fa-house';
            case DataFieldPropertyType.Roles:
                return 'fas fa-user-group';
            case DataFieldPropertyType.Units:
                return 'fas fa-shop fs-12';
            case DataFieldPropertyType.Tags:
                return 'fas fa-user-tag';
            case DataFieldPropertyType.PKOrgNr:
                return 'fas fa-hashtag';
            case DataFieldPropertyType.Custom:
                return this.getProfileViewCustomDataFieldsMatIconClass(dataField);
        }
    }

    private getProfileViewCustomDataFieldsMatIconClass(dataField: DataFieldModel): string {
        switch (dataField.Type) {
            case DataFieldType.Text:
                return 'fas fa-square-poll-horizontal';
            case DataFieldType.Numeric:
                return 'fas fa-calculator';
            case DataFieldType.Calendar:
                return 'fas fa-calendar-day';
            case DataFieldType.PreSetOptions:
                return 'fas fa-clipboard-list';
        }
    }

    isUserDataFieldShownIfProfileView(dataField: DataFieldModel, userModel: UserModel): boolean {
        switch (dataField.PropertyType) {
            case DataFieldPropertyType.Roles:
                return !!userModel.Roles?.length;
            case DataFieldPropertyType.Units:
                return !!userModel.BusinessUnits?.length;
            case DataFieldPropertyType.BusinessAreas:
                return !!userModel.BusinessAreas?.length;
            case DataFieldPropertyType.Tags:
                return !!userModel.Tags?.length;
            case DataFieldPropertyType.Areas:
                return !!userModel.Areas?.length;
            case DataFieldPropertyType.EmailAddress:
                return !!userModel.EmailAddressContacts?.length;
            case DataFieldPropertyType.PhoneNumber:
                return !!userModel.PhoneNumberContacts?.length;
            case DataFieldPropertyType.Address:
                return !!userModel.Addresses?.length;
            case DataFieldPropertyType.Custom:
                return this.isCustomDataFieldShownInProfileView(dataField);
        }
    }

    isBusinessUnitDataFieldShownInProfileView(dataField: DataFieldModel, businessUnit: BusinessUnitModel): boolean {
        switch (dataField.PropertyType) {
            case DataFieldPropertyType.EmailAddress:
                return !!businessUnit.EmailAddressContacts?.length;
            case DataFieldPropertyType.PhoneNumber:
                return !!businessUnit.PhoneNumberContacts?.length;
            case DataFieldPropertyType.Address:
                return !!businessUnit.Addresses?.length;
            case DataFieldPropertyType.Description:
                return !!businessUnit.Description?.length;
            case DataFieldPropertyType.Areas:
                return !!businessUnit.Area;
            case DataFieldPropertyType.Cluster:
                return !!businessUnit.Cluster && !!businessUnit.Cluster.Id;
            case DataFieldPropertyType.BusinessAreas:
                return !!businessUnit.BusinessAreas?.length;
            case DataFieldPropertyType.PKOrgNr:
                return !!businessUnit.OrganisationNumber;
            case DataFieldPropertyType.Custom:
                return this.isCustomDataFieldShownInProfileView(dataField);
        }
    }

    private isCustomDataFieldShownInProfileView(dataField: DataFieldModel): boolean {
        switch (dataField.Type) {
            case DataFieldType.Text:
            case DataFieldType.Numeric:
            case DataFieldType.Calendar:
                return !!dataField.Value;
            case DataFieldType.PreSetOptions:
                return !!dataField.PreSetValues?.length;
        }
    }

    isCustomDataFieldValueDefined(profileDataFields: DataFieldModel[]): boolean {
        if (!profileDataFields) return false;
        return !!profileDataFields.find((profileDataField) => {
            return (
                profileDataField.PropertyType === DataFieldPropertyType.Custom &&
                (profileDataField.Value || profileDataField.PreSetValues?.length)
            );
        });
    }
}
